import ky from 'ky'
import type { MoteFinalizeResponse, RegisterMoteResponse } from '@mote/api'
import { apiClient } from '@mote/api'
import { assert } from '../../asserts'
import type { PayloadInjector } from '../tracker'
import { Chunker } from './Chunker'
import { TimeoutScheduler } from './TimeoutScheduler'
import { ChunkUploadError, UploadQueue } from './UploadQueue'

const FINALIZE_TIMEOUT_MILLISECONDS = 600_000
const NETWORK_CONGESTION_TIMEOUT_MILLISECONDS = 5_000

export class NetworkManager extends EventTarget {
  #started = false
  #canceled = false
  #maybeMoteId: string | null = null
  #apiClient: typeof apiClient = apiClient.extend({ retry: 0, timeout: false })
  #chunker = new Chunker()
  #uploadQueue = new UploadQueue(error => {
    this.dispatchEvent(new ErrorEvent('error', { error }))
  })
  #finalizeTimeoutScheduler = new TimeoutScheduler(
    secondsFromStart =>
      this.#handleUploadQueueError(
        new ChunkUploadError(
          this.#uploadQueue.introspection,
          secondsFromStart,
          'timeout'
        )
      ),
    FINALIZE_TIMEOUT_MILLISECONDS
  )
  #networkCongestionTimeoutScheduler = new TimeoutScheduler(
    secondsFromStart => this.#handleNetworkCongestion(secondsFromStart),
    NETWORK_CONGESTION_TIMEOUT_MILLISECONDS
  )

  readonly #maybeRegisterPromise: Promise<RegisterMoteResponse | null> | null =
    null
  #registerPromiseResolver:
    | ((withMoteStatus: RegisterMoteResponse | null) => void)
    | null = null
  readonly #payloadInjector: PayloadInjector

  constructor(payloadInjector: PayloadInjector) {
    super()
    this.#payloadInjector = payloadInjector
    this.#maybeRegisterPromise = new Promise<RegisterMoteResponse | null>(
      resolve => {
        this.#registerPromiseResolver = resolve
      }
    )
  }

  async register() {
    return this.#apiClient
      .post('v1/m/register', {
        json: {
          ...(await this.#payloadInjector.getRegisterPayload()),
          TZOffset: new Date().getTimezoneOffset()
        }
      })
      .then(async response => {
        const withMoteStatus: RegisterMoteResponse = await response.json()
        this.#started = true
        this.#maybeMoteId = withMoteStatus.moteStatus.moteId
        this.#uploadQueue.start(`v1/m/addChunk/${this.#moteId}`)
        if (this.#registerPromiseResolver) {
          this.#registerPromiseResolver(withMoteStatus)
        }
        return withMoteStatus
      })
      .catch(async errorResponse => {
        this.#started = false
        this.#uploadQueue.abort()
        let error = new RegisterError()
        if (errorResponse instanceof ky.HTTPError) {
          try {
            const errorJson = await errorResponse.response.json()
            error = errorFormReason(errorJson.reason)
          } catch {}
        }
        this.dispatchEvent(new ErrorEvent('error', { error }))
        if (this.#registerPromiseResolver) {
          this.#registerPromiseResolver(null)
        }
        return null
      })
  }

  async finalize() {
    this.#finalizeTimeoutScheduler.start()
    this.#networkCongestionTimeoutScheduler.start()
    await this.#registerPromise
    try {
      await this.#uploadQueue.complete()
      const finalizeResponse = await this.#finalize()
      this.#finalizeTimeoutScheduler.stop()
      this.#networkCongestionTimeoutScheduler.stop()
      return [finalizeResponse, this.#chunker.blobs] as const
    } catch (e) {
      this.#finalizeTimeoutScheduler.stop()
      this.#networkCongestionTimeoutScheduler.stop()
      throw e
    }
  }

  async pause() {
    await this.#registerPromise
    if (!this.#started || this.#canceled) {
      return
    }
    await this.#uploadQueue.pause()
    return this.#apiClient.post('v1/m/pause', {
      json: {
        ...this.#payloadInjector.getPausePayload(),
        moteId: this.#moteId,
        latestChunk: this.#chunker.latestId
      }
    })
  }

  async cancel() {
    this.#canceled = true
    await this.#registerPromise
    if (!this.#started) {
      return
    }
    this.#uploadQueue.abort()
    return this.#apiClient.post('v1/m/cancel', {
      json: {
        ...this.#payloadInjector.getCancelPayload(),
        moteId: this.#moteId,
        lastChunkId: this.#chunker.latestId
      }
    })
  }

  send(blob: Blob) {
    this.#chunker.add(blob)
    this.#uploadQueue.add(this.#chunker.latest!)
  }

  fetchMote() {
    return this.#registerPromise
  }

  async #finalize(retryCount = 0): Promise<MoteFinalizeResponse> {
    try {
      const response = await this.#apiClient.post(
        `v1/m/finalise/${this.#moteId}`,
        {
          timeout: 5000,
          json: {
            ...this.#payloadInjector.getFinalizePayload(),
            summary: {
              chunks: this.#chunker.count,
              bytes: this.#chunker.size
            }
          }
        }
      )
      return await response.json()
    } catch (e) {
      if (retryCount > 0) {
        throw new MoteFinalizationError()
      }
      return this.#finalize(retryCount + 1)
    }
  }

  get #registerPromise() {
    assert(
      this.#maybeRegisterPromise !== null,
      'this.#maybeRegisterPromise not defined yet, you have to call .register before'
    )
    return this.#maybeRegisterPromise
  }

  get #moteId() {
    assert(
      this.#maybeMoteId !== null,
      'this.#maybeMoteId not defined yet, missing register call'
    )
    return this.#maybeMoteId
  }

  #handleUploadQueueError(error: ChunkUploadError) {
    this.#networkCongestionTimeoutScheduler.stop()
    this.#uploadQueue.abort()
    this.dispatchEvent(new ErrorEvent('error', { error }))
    return this.#apiClient.post(`v1/m/delete/${this.#moteId}`).catch(() => {})
  }

  #handleNetworkCongestion(secondsFromStart: number) {
    this.#networkCongestionTimeoutScheduler.resume()
    this.dispatchEvent(
      new CustomEvent<NetworkCongestionEventDetail>('networkcongestion', {
        detail: {
          assetId: this.#maybeMoteId || 'UNDEFINED',
          secondsSinceClickingStop: secondsFromStart
        }
      })
    )
  }
}

export class NetworkManagerError extends Error {
  constructor(message?: string) {
    super(message)
    Object.setPrototypeOf(this, NetworkManagerError.prototype)
  }
}

export class RegisterError extends NetworkManagerError {
  constructor() {
    super('Register Failure')
    Object.setPrototypeOf(this, RegisterError.prototype)
  }
}

export class QuotaExhaustedRegisterError extends NetworkManagerError {
  constructor() {
    super('Quota exhausted')
    Object.setPrototypeOf(this, QuotaExhaustedRegisterError.prototype)
  }
}

export class NotLoggedInRegisterError extends NetworkManagerError {
  constructor(reason: string) {
    super(reason)
    Object.setPrototypeOf(this, NotLoggedInRegisterError.prototype)
  }
}

export class MoteFinalizationError extends NetworkManagerError {
  errorCode = 'mote_finalization_failed'
  constructor() {
    super('Mote finalization failed')
    Object.setPrototypeOf(this, MoteFinalizationError.prototype)
  }
}

function errorFormReason(reason: string) {
  if (reason === 'Quota exhausted') {
    return new QuotaExhaustedRegisterError()
  }
  if (reason === 'deleted token') {
    return new NotLoggedInRegisterError('Deleted token')
  }
  if (reason === 'no populated accessToken') {
    return new NotLoggedInRegisterError('No populated accessToken')
  }
  throw Error(`Reason not defined: ${reason}`)
}

export interface NetworkCongestionEventDetail {
  assetId: string
  secondsSinceClickingStop: number
}

export type NetworkCongestionEvent = CustomEvent<NetworkCongestionEventDetail>
